/**
 * Enum vocabularies for the Delivery Runs console. Each is a runtime tuple (for
 * filter dropdowns, `validateSearch`, and iteration) paired with a derived
 * string-literal union so the wire values and the TS types can never drift.
 * These mirror the integration-service Delivery API contract (camelCase wire).
 */

/** Top-level delivery workflow family. */
export const DELIVERY_FAMILIES = ['PRODUCT_PUSH', 'FEED_FILE'] as const;
export type DeliveryFamily = (typeof DELIVERY_FAMILIES)[number];

/** Execution path within a family — selects the concrete Temporal workflow. */
export const DELIVERY_SUB_VARIANTS = [
  'API',
  'SFTP',
  'FEEDONOMICS',
  'GMC_EXPORT',
  'META_SUPPLEMENTAL',
] as const;
export type DeliverySubVariant = (typeof DELIVERY_SUB_VARIANTS)[number];

/**
 * How a delivery is dispatched (trigger request `deliveryMethod`). Closed enum
 * server-side — an unknown value is a 422. Omitting the field defaults to `API`;
 * an explicit `null` is rejected, so never serialise an unset method as null.
 */
export const DELIVERY_METHODS = [
  'API',
  'FILE',
  'SFTP',
  'FEEDONOMICS',
  'SUPPLEMENTAL_FEED',
] as const;
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number];

/**
 * Run lifecycle status. The first block is the coarse set (terminal members
 * bolded in the contract); the second is the 8 granular in-flight stages, which
 * the UI treats as RUNNING (see `IN_FLIGHT_RUN_STATUSES`).
 */
export const PIPELINE_RUN_STATUSES = [
  'PENDING',
  'RUNNING',
  'PAUSED',
  'RETRYING',
  'COMPLETED',
  'DELIVERED',
  'PARTIAL_DELIVERY',
  'FAILED',
  'DELIVERY_FAILED',
  'CANCELLED',
  // Granular in-flight stages — rendered as RUNNING.
  'VALIDATING_SOURCE_DATA',
  'GENERATING_FEED_FILE',
  'UPLOADING_TO_STAGING',
  'GENERATING_DELIVERY_ACCESS',
  'REGISTERING_FEED_DESTINATION',
  'DISPATCHING_TO_MERCHANT',
  'CONFIRMING_MERCHANT_RECEIPT',
  'FINALIZING_DELIVERY',
] as const;
export type PipelineRunStatus = (typeof PIPELINE_RUN_STATUSES)[number];

/**
 * Granular stages that mean "still running" — collapse them for the raw-status
 * tone map. Terminal/running classification of a *run* comes from the server's
 * `phase` / `isTerminal`, never a hardcoded set.
 */
export const IN_FLIGHT_RUN_STATUSES = new Set<PipelineRunStatus>([
  'VALIDATING_SOURCE_DATA',
  'GENERATING_FEED_FILE',
  'UPLOADING_TO_STAGING',
  'GENERATING_DELIVERY_ACCESS',
  'REGISTERING_FEED_DESTINATION',
  'DISPATCHING_TO_MERCHANT',
  'CONFIRMING_MERCHANT_RECEIPT',
  'FINALIZING_DELIVERY',
]);

/** Per-product lifecycle status on a delivery audit row / run leaf. Never empty —
 *  an unclassifiable row reads `UNKNOWN`, not `''`. NOT the outcome filter menu:
 *  `PENDING` / `STALE` / `INCLUDED` are states, not outcomes, and the leaf
 *  endpoints 422 on them — the menu is sourced from `GET /deliveries/runs/product-outcomes`. */
export const PRODUCT_DELIVERY_STATUSES = [
  'PENDING',
  'DELIVERED',
  'FAILED',
  'SKIPPED_UNCHANGED',
  'VALIDATION_FAILED',
  'STALE',
  'EXCLUDED',
  'INCLUDED',
  'UNKNOWN',
] as const;
export type ProductDeliveryStatus = (typeof PRODUCT_DELIVERY_STATUSES)[number];

/**
 * UI-facing run lifecycle phase — a closed set the server derives from the 17
 * raw statuses (8 of which are just GMC-export step names). Switch on this, not
 * `status`; poll on the run's `isTerminal` rather than hardcoding the terminal
 * subset. An unrecognised status degrades to `PROCESSING`, never a terminal phase.
 */
export const DELIVERY_PHASES = [
  'PENDING',
  'VALIDATING',
  'PROCESSING',
  'DELIVERING',
  'PAUSED',
  'RETRYING',
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'FAILED',
  'CANCELLED',
] as const;
export type DeliveryPhase = (typeof DELIVERY_PHASES)[number];

/** Destination sync-review verdict on a product leaf. `NOT_APPLICABLE` = this
 *  run never dispatched the product, so no verdict is coming. */
export const SYNC_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'UNKNOWN',
  'NOT_APPLICABLE',
] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

/** Actions a run can accept *right now* (`run.availableActions`). Drive control
 *  buttons off this, not the static `capabilities` matrix. */
export const AVAILABLE_ACTIONS = [
  'PAUSE',
  'RESUME',
  'CANCEL',
  'RETRY_FAILED',
  'VIEW_FAILURES',
  'VIEW_REPORT',
  'VIEW_TIMELINE',
] as const;
export type AvailableAction = (typeof AVAILABLE_ACTIONS)[number];

/** Closed set of reconciled timeline-step statuses (lowercase on the wire). */
export const TIMELINE_STEP_STATUSES = [
  'running',
  'done',
  'failed',
  'cancelled',
  'paused',
  'pending',
] as const;
export type TimelineStepStatus = (typeof TIMELINE_STEP_STATUSES)[number];

/** Trigger-response job status (distinct from the run lifecycle status). */
export const DELIVERY_JOB_STATUSES = [
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'FAILED',
  'CANCELLED',
] as const;
export type DeliveryJobStatus = (typeof DELIVERY_JOB_STATUSES)[number];

/**
 * Outcome-bucket reconciliation verdict (`GET /runs/{id}/reconcile`). This enum
 * has grown twice — render an unrecognised value defensively (muted, raw label).
 * `SOURCE_MISMATCH` (BE PR #620): the run's own totals balance but the
 * independent per-chunk ledger disagrees — a bookkeeping fault, not lost products.
 */
export const RECONCILIATION_STATUSES = [
  'MATCH',
  'MISMATCH',
  'SOURCE_MISMATCH',
  'SKIPPED',
  'UNKNOWN',
] as const;
export type ReconciliationStatus = (typeof RECONCILIATION_STATUSES)[number];

/** How the run's totals and the work-unit ledger disagree (`reconcile.sources.crossSource.kind`). */
export const CROSS_SOURCE_KINDS = ['NONE', 'COVERAGE_GAP', 'BUCKET_DISAGREEMENT', 'BOTH'] as const;
export type CrossSourceKind = (typeof CROSS_SOURCE_KINDS)[number];

/** Sort options for the run list (`GET /runs?sortBy=`). */
export const RUN_SORT_OPTIONS = ['date_desc', 'date_asc', 'duration', 'products'] as const;
export type RunSortBy = (typeof RUN_SORT_OPTIONS)[number];

/** Sort options for the product-leaf endpoints. */
export const PRODUCT_LEAF_SORT_OPTIONS = ['date_desc', 'status'] as const;
export type ProductLeafSortBy = (typeof PRODUCT_LEAF_SORT_OPTIONS)[number];

/** Feed file format for FILE/SFTP deliveries. */
export const FEED_FORMATS = ['TSV', 'CSV'] as const;
export type FeedFormat = (typeof FEED_FORMATS)[number];

/** Feedonomics feed profile (FEEDONOMICS method only). */
export const FEEDONOMICS_FEED_PROFILES = ['ENRICHED', 'SUPPLEMENTARY'] as const;
export type FeedonomicsFeedProfile = (typeof FEEDONOMICS_FEED_PROFILES)[number];

// ---------------------------------------------------------------------------
// Meta delivery workspace — per-product ingest vocabularies (UI contract 2026-09-10)
// ---------------------------------------------------------------------------

/**
 * Where a product got to on a Meta delivery (`ingestStatus`). `UPLOADED` is the
 * furthest the platform can honestly claim today — Meta queued the item and the
 * per-item outcome is unknown — so it must never be rendered as "live on Meta".
 * `PERSISTED` / `CONFIRM_PENDING` (P1) and `AWAITING_OWNERSHIP` (P2) are specced
 * but not yet written. The empty string means nothing was ingested this run
 * (e.g. skipped-unchanged) — not an error.
 */
export const INGEST_STATUSES = [
  'QUEUED',
  'WITHHELD',
  'UPLOADED',
  'ROW_REJECTED',
  'TRANSPORT_FAILED',
  'PERSISTED',
  'CONFIRM_PENDING',
  'AWAITING_OWNERSHIP',
  '',
] as const;
export type IngestStatus = (typeof INGEST_STATUSES)[number];

/** Known machine sub-reasons (`ingestReason`). Open-ended on the wire — it also
 *  carries Meta's numeric Graph codes as strings and gains values as P1 lands,
 *  so always fall back to the raw string + `ingestMessage`, never a blank. */
export const KNOWN_INGEST_REASONS = [
  'SKIPPED_NO_ID',
  'SKIPPED_ONLY_ID',
  'VALIDATION_FAILED',
  'EXCLUDED_FROM_FEED',
  // GMC (BE PR #703)
  'NOT_IN_EXPORT_ARTIFACT',
  'NOT_IN_PRIMARY_FEED',
  'FEED_PUSH_FAILED',
] as const;
export type KnownIngestReason = (typeof KNOWN_INGEST_REASONS)[number];

/** `REAL` or `MOCK`. A MOCK record comes from a dry run and must never be
 *  presented as a real delivery. */
export const RUN_MODES = ['REAL', 'MOCK'] as const;
export type RunMode = (typeof RUN_MODES)[number];

/** Our own pre-send validation gate on an audit row. */
export const VALIDATION_STATUSES = ['PASSED', 'FAILED', 'SKIPPED'] as const;
export type ValidationStatus = (typeof VALIDATION_STATUSES)[number];

/** Which identity `productId` matches on the timeline lookup. There is deliberately
 *  no platform-UUID mode — the audit store has no such column. `external` (BE PR
 *  #703) is the destination-side id: for GMC the offer id, matched across every
 *  shape GMC rows store it in (`OFFER-1`, `online:en:US:OFFER-1`,
 *  `accounts/…/products/en~US~OFFER-1`) — `retailer` only matches GMC feed-file rows. */
export const TIMELINE_ID_TYPES = ['retailer', 'source', 'external'] as const;
export type TimelineIdType = (typeof TIMELINE_ID_TYPES)[number];

/** Meta connection `config.delivery_method` values. Feed generation requires
 *  `SUPPLEMENTAL_FEED`; `API` routes to items_batch instead. */
export const META_DELIVERY_METHODS = ['API', 'SUPPLEMENTAL_FEED'] as const;
export type MetaDeliveryMethod = (typeof META_DELIVERY_METHODS)[number];
